import React, { useContext, useEffect } from "react";
import useMedia from "utils/hooks/useMedia";
import NewLookPlanCarousel from "features/settings/Subscription/NewLook/NewLookPlanCarousel.tsx";
import styled, { css } from "styled-components";
import { Redirect, useHistory } from "react-router-dom";
import { Check } from "lucide-react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { LanguageNameSpaces, SubscriptionPlanType } from "@shared/types";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import {
  BASE_BILLING_SETTINGS_PATH,
  PAGE_SPINNER_MARGIN_FROM_TOP,
} from "common/constants";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useMeasure from "utils/hooks/useMeasure.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useChoosePlan from "features/settings/Subscription/useChoosePlan.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import NewLookPlanCard from "features/settings/Subscription/NewLook/NewLookPlanCard.tsx";
import NewLookPeriodToggle from "features/settings/Subscription/NewLook/NewLookPeriodToggle.tsx";
import NewLookBackLink from "features/settings/NewLook/NewLookBackLink.tsx";

const AVAILABLE_ALL_PLAN_FEATURES: TranslationKeys<LanguageNameSpaces.BILLING>[] =
  [
    "choose_plan.mobile_web_apps",
    "choose_plan.support_24_7",
    "choose_plan.automated_trading",
    "choose_plan.advanced_trading_features",
  ];

const ENTERPRISE_PLAN_DESCRIPTION_KEYS: Partial<
  Record<SubscriptionPlanType, TranslationKeys<LanguageNameSpaces.BILLING>>
> = {
  [SubscriptionPlanType.ASSET_MANAGER]:
    "enterprise_plan_descriptions.asset_manager",
  [SubscriptionPlanType.COMMUNITY_ADMIN]:
    "enterprise_plan_descriptions.community_admin",
};

const GAP = 16;
const MIN_PLAN_WIDTH = 250;
const MAX_PLAN_WIDTH = 380;
/** Below this a business card stacks its price and action like a tier card. */
const MIN_WIDE_BUSINESS_CARD = 520;

/** How many plan cards fit next to each other (no lone orphan rows). */
const getColumns = (width: number, count: number) => {
  let cols = Math.floor((width + GAP) / (MIN_PLAN_WIDTH + GAP));
  cols = Math.max(1, Math.min(cols, count));
  if (count === 4 && cols === 3) cols = 2;
  if (count === 3 && cols === 2) cols = 1;
  return cols;
};

/**
 * The plan picker in the new look: a centred header with the billing period
 * toggle, the regular plans side by side and the business plans below.
 */
const NewLookChoosePlan = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { isUpgrade, analyticsInvoiceType } = useContext(
    BuySubscriptionContext
  )!;
  const {
    isFetching,
    filteredPlans,
    getIsAllowChoosePlan,
    promoCodeDiscount,
    title,
  } = useChoosePlan();
  const history = useHistory();
  const { analytics } = useContext(AnalyticsContext);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const isPhone = useMedia(["(max-width: 600px)"], [true], false);

  const redirect = getIsAllowChoosePlan()
    ? undefined
    : BASE_BILLING_SETTINGS_PATH;

  useEffect(() => {
    if (!redirect) {
      analytics?.purchasePlanChoosePlanPageView({
        subscriptionInvoiceType: analyticsInvoiceType,
      });
    }
  }, [analytics, redirect]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  const regularPlans = filteredPlans.filter(
    (plan) => !isWhitelabelSubscriptionType(plan.subscription_plan_type)
  );
  const enterprisePlans = filteredPlans.filter((plan) =>
    isWhitelabelSubscriptionType(plan.subscription_plan_type)
  );
  // A lone tier (e.g. upgrading to Premium) shares one row with the
  // business plans instead of sitting above two squeezed wide cards.
  const isSingleRow = regularPlans.length === 1 && enterprisePlans.length > 0;
  const regularCols = getColumns(
    width,
    isSingleRow ? filteredPlans.length : regularPlans.length
  );
  const enterpriseCols = width >= 2 * 420 + GAP ? 2 : 1;
  const gridMaxWidth = regularCols * MAX_PLAN_WIDTH + (regularCols - 1) * GAP;
  // The business row spans the tier grid above so both share their edges.
  const enterpriseGridWidth = Math.min(width, Math.max(gridMaxWidth, 760));
  const isCompactEnterprise =
    isSingleRow ||
    (enterpriseGridWidth - (enterpriseCols - 1) * GAP) / enterpriseCols <
      MIN_WIDE_BUSINESS_CARD;
  const getEnterpriseCard = (plan: (typeof filteredPlans)[number]) => {
    const descriptionKey =
      ENTERPRISE_PLAN_DESCRIPTION_KEYS[plan.subscription_plan_type!];
    return (
      <NewLookPlanCard
        key={plan.subscription_plan_type}
        plan={plan}
        isUpgrade={isUpgrade}
        promoCodeDiscount={promoCodeDiscount}
        description={descriptionKey ? t(descriptionKey) : ""}
        isEnterprise={true}
        isCompact={isCompactEnterprise}
      />
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={isFetching}
      spinnerSize="large"
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      hideContentWhileLoading={isFetching}
    >
      <Page $isBottomNavigator={isBottomNavigator}>
        <NewLookBackLink
          onClick={() => history.push(BASE_BILLING_SETTINGS_PATH)}
        >
          {t("cancel_subscription.back_to_billing")}
        </NewLookBackLink>
        <Header>
          <Title>{title}</Title>
          {!isUpgrade && (
            <NewLookPeriodToggle promoCodeDiscount={promoCodeDiscount} />
          )}
          <Included $afterToggle={!isUpgrade}>
            <IncludedLabel>
              {t("choose_plan.available_on_all_plans")}
            </IncludedLabel>
            {AVAILABLE_ALL_PLAN_FEATURES.map((key) => (
              <IncludedItem key={key}>
                <Check size={14} strokeWidth={2.25} />
                {t(key)}
              </IncludedItem>
            ))}
          </Included>
        </Header>
        <Grids ref={measureRef}>
          {isPhone && !isSingleRow && regularPlans.length > 1 ? (
            <NewLookPlanCarousel
              plans={regularPlans}
              isUpgrade={isUpgrade}
              promoCodeDiscount={promoCodeDiscount}
            />
          ) : (
            regularPlans.length > 0 && (
              <Grid $cols={regularCols} style={{ maxWidth: gridMaxWidth }}>
                {regularPlans.map((plan) => (
                  <NewLookPlanCard
                    key={plan.subscription_plan_type}
                    plan={plan}
                    isUpgrade={isUpgrade}
                    promoCodeDiscount={promoCodeDiscount}
                  />
                ))}
                {isSingleRow && enterprisePlans.map(getEnterpriseCard)}
              </Grid>
            )
          )}
          {enterprisePlans.length > 0 && !isSingleRow && (
            <Grid
              $cols={enterpriseCols}
              style={{ maxWidth: enterpriseGridWidth }}
            >
              {enterprisePlans.map(getEnterpriseCard)}
            </Grid>
          )}
        </Grids>
      </Page>
    </LoadingSpinnerOverlay>
  );
};

export default NewLookChoosePlan;

const Page = styled.div<{ $isBottomNavigator: boolean }>`
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} 48px;
  user-select: none;
  ${(p) =>
    p.$isBottomNavigator &&
    css`
      padding: 12px 16px 32px;
    `}
`;
/**
 * Title → 16 → period toggle (its saving note hangs 8 below it) → 24 → the
 * "available on all plans" line → 20 → the plan cards.
 */
const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: 4px auto 20px;
  text-align: center;
`;
const Title = styled.h1`
  margin: 0;
  ${newLookType.pageTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Included = styled.div<{ $afterToggle: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px 20px;
  max-width: 960px;
  margin-top: ${(p) => (p.$afterToggle ? 8 : 0)}px;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).text};
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 8px 12px;
    text-align: left;
  }
`;
const IncludedLabel = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  @media (max-width: 600px) {
    grid-column: 1 / -1;
    text-align: center;
  }
`;
const IncludedItem = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  svg {
    flex: none;
    margin-top: 1px;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;
const Grids = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${GAP}px;
  width: 100%;
`;
const Grid = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, minmax(0, 1fr));
  gap: ${GAP}px;
  width: 100%;
`;
